import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { object, number, string, array, InferType, boolean } from "yup";
import { FormProvider, useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Spinner } from "@heroui/react";
import {
    useMemo,
    useState,
    useCallback,
    SetStateAction,
    Dispatch,
    useEffect,
} from "react";
import {
    ColumnDef,
    OnChangeFn,
    Row,
    RowSelectionState,
    Updater,
} from "@tanstack/react-table";
import { ApplicationMethod, NitrogenSimulationType } from "@/types/enums";
import {
    ApplicationPlan,
    PlantingPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import NumberInput from "../../inputs/NumberInput";
import SelectInput from "../../inputs/SelectInput";
import TextInput from "@/components/inputs/TextInput";
import SwitchInput from "@/components/inputs/SwitchInput";
import ExpandingTable from "@/components/table/ExpandingTable";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import {
    faCheckCircle,
    faCopy,
    faExclamationTriangle,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CropConfiguration, CropStage } from "@/types/cropConfiguration";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { AxiosRequestConfig } from "axios";
import { getSimulationTypeLabel } from "@/lib/utils/nitrogenPlan";
import { ConfirmPrimaryPlan } from "./ConfirmPlan";
import { applicationPlanColumns } from "./plannerColumns";
import { ProcessingStatus } from "@/types/fieldNitrogenPlan";
import useConfirm from "@/lib/confirm/useConfirm";
import ProductAutoComplete from "@/components/inputs/ProductAutoComplete";

const SENTINEL_ORGANIZATION_ID = 1;

const applicationMethodOptions = [
    {
        id: ApplicationMethod.InjectedKnifedCoultered.toString(),
        description: "Injected/Knifed/Coultered",
    },
    { id: ApplicationMethod.Dribble.toString(), description: "Dribble" },
    { id: ApplicationMethod.Broadcast.toString(), description: "Broadcast" },
    { id: ApplicationMethod.Topdress.toString(), description: "Topdress" },
    { id: ApplicationMethod.YDrop.toString(), description: "Y-Drop" },
    {
        id: ApplicationMethod.BroadcastTillage.toString(),
        description: "Broadcast Tillage",
    },
    { id: ApplicationMethod.TwoXTwo.toString(), description: "2x2" },
    { id: ApplicationMethod.Furrow.toString(), description: "Furrow" },
    {
        id: ApplicationMethod.Fertigation.toString(),
        description: "Fertigation",
    },
];

const applicationPlanSchema = object({
    id: number().nullable(),
    name: string().required(),
    organization: number().required(),
    planned_applications: array()
        .of(
            object({
                id: number().nullable(),
                target_crop_stage: number().required(),
                method: string()
                    .oneOf([
                        ApplicationMethod.InjectedKnifedCoultered,
                        ApplicationMethod.Dribble,
                        ApplicationMethod.Broadcast,
                        ApplicationMethod.Topdress,
                        ApplicationMethod.YDrop,
                        ApplicationMethod.BroadcastTillage,
                        ApplicationMethod.TwoXTwo,
                        ApplicationMethod.Furrow,
                        ApplicationMethod.Fertigation,
                    ])
                    .required(),
                user_rate: number().nullable(),
                application_cost: number().nullable(),
                fertilizer_product: string().required(),
                model_rate: number().nullable(),
                establish_plots: boolean().required(),
            }),
        )
        .min(1, "At least one application is required")
        .required(),
});

type ApplicationPlanFormValues = InferType<typeof applicationPlanSchema>;
export function ApplicationPlanForm({
    organizations,
    selectedApplicationPlanIds,
    setSelectedApplicationPlanIds,
    applicationPlansRequest,
    cropStageOptions,
    cropPlansRequest,
    simulationType,
    plantingPlan,
    simulateCropPlansMutation,
    fields,
    primaryCropPlan,
}: {
    fields: { id: number; active_planting_operation: { id: number } | null }[];
    organizations: { id: number; description: string }[];
    selectedApplicationPlanIds: number[];
    setSelectedApplicationPlanIds: Dispatch<SetStateAction<number[]>>;
    applicationPlansRequest: UseQueryResult<ApplicationPlan[]>;
    cropStageOptions: CropStage[];
    plantingPlan: PlantingPlan;
    cropPlansRequest: UseQueryResult<SimulatedCropPlan[]>;
    simulationType: NitrogenSimulationType;
    selectionMode?: "single" | "multiple";
    setPrimaryPlan?: boolean;
    onClose?: () => void;
    selectedCropConfiguration?: CropConfiguration;
    simulateCropPlansMutation: UseMutationResult<
        SimulatedCropPlan[],
        Error,
        AxiosRequestConfig,
        unknown
    >;
    primaryCropPlan?: SimulatedCropPlan;
}) {
    const { useMutateRequest } = useRequest();

    const { user, isGrower, userIsSentinel } = useAuth();
    const userOrganizationId: number | undefined = useMemo(() => {
        if (typeof user?.organization === "object") {
            return user.organization.id;
        }
        return typeof user?.organization === "string"
            ? parseInt(user.organization)
            : user?.organization;
    }, [user]);

    const applicationPlanForm = useForm<ApplicationPlanFormValues>({
        resolver: yupResolver(applicationPlanSchema),
        defaultValues: {
            id: null,
            organization: userOrganizationId,
            planned_applications: [
                {
                    id: null,
                    method: ApplicationMethod.InjectedKnifedCoultered,
                    fertilizer_product: "2",
                    user_rate: null,
                    application_cost: null,
                    establish_plots: false,
                },
            ],
        },
    });

    const [applicationPlanId, applicationPlanOrganization] =
        applicationPlanForm.watch(["id", "organization"]);

    const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
    const [addNewPlan, setAddNewPlan] = useState(false);

    const {
        fields: applicationFields,
        remove,
        append,
    } = useFieldArray({
        control: applicationPlanForm.control,
        name: "planned_applications",
    });

    const saveApplicationPlanMutation = useMutateRequest<ApplicationPlan>({
        successMessage: "Application Plan Saved",
        onSuccess: () => {
            applicationPlansRequest.refetch();
        },
    });

    const handleSaveApplicationPlan = useCallback(
        (onSuccess?: (applicationPlanId: number) => void) => {
            if (
                !applicationPlanForm.formState.isDirty &&
                applicationPlanForm.getValues("id") !== null
            ) {
                onSuccess?.(applicationPlanForm.getValues("id") as number);
                return;
            }
            const applicationData = {
                id: applicationPlanId,
                organization: applicationPlanForm.getValues("organization"),
                name: applicationPlanForm.getValues("name"),
                planned_applications: applicationPlanForm
                    .getValues("planned_applications")
                    .map((application) => ({
                        id: application.id,
                        target_crop_stage: application.target_crop_stage,
                        method: application.method,
                        user_rate: Number(application.user_rate) || null,
                        application_cost: application.application_cost,
                        fertilizer_product: application.fertilizer_product,
                        establish_plots: application.establish_plots,
                    })),
            };
            if (applicationPlanId) {
                saveApplicationPlanMutation.mutate(
                    {
                        url: `/application_plans/${applicationPlanId}/`,
                        method: "PUT",
                        data: applicationData,
                    },
                    {
                        onSuccess: (response) => {
                            onSuccess?.(response.id);
                        },
                    },
                );
            } else {
                saveApplicationPlanMutation.mutate(
                    {
                        url: `/application_plans/`,
                        method: "POST",
                        data: applicationData,
                    },
                    {
                        onSuccess: (response) => {
                            onSuccess?.(response.id);
                        },
                    },
                );
            }
        },
        [applicationPlanForm, saveApplicationPlanMutation, applicationPlanId],
    );

    const simulatedPlanStatus = useMemo(() => {
        return (
            cropPlansRequest.data?.find(
                (cp) =>
                    cp.application_plan?.id === editingPlanId &&
                    cp.simulation_type === simulationType,
            )?.processing_status ?? undefined
        );
    }, [cropPlansRequest.data, editingPlanId, simulationType]);

    useEffect(() => {
        if (editingPlanId) {
            const simulatedPlan = cropPlansRequest.data?.find(
                (cp) =>
                    cp.application_plan?.id === editingPlanId &&
                    cp.simulation_type === simulationType,
            );
            if (!simulatedPlan) return;

            const applicationPlan = simulatedPlan.application_plan;
            applicationPlanForm.reset({
                id: editingPlanId,
                name: applicationPlan?.name ?? "",
                organization:
                    applicationPlan?.organization ?? userOrganizationId,
                planned_applications:
                    applicationPlan?.planned_applications?.length &&
                        applicationPlan?.planned_applications?.length > 0
                        ? sortArrayByKey(
                            applicationPlan?.planned_applications ?? [],
                            (app) =>
                                cropStageOptions.find(
                                    (cs) => cs.id === app.target_crop_stage,
                                )?.days_after_planting,
                        ).map((app) => ({
                            id: app.id,
                            target_crop_stage: app.target_crop_stage,
                            method: app.method,
                            fertilizer_product:
                                app.fertilizer_product.toString(),
                            user_rate: app.user_rate,
                            application_cost: app.application_cost,
                            establish_plots: app.establish_plots,
                            model_rate:
                                simulatedPlan?.simulated_rates?.find(
                                    (rate) =>
                                        rate.planned_application === app.id &&
                                        simulatedPlan.processing_status ===
                                        ProcessingStatus.COMPLETED,
                                )?.optimal_rate ?? null,
                        }))
                        : [
                            {
                                id: null,
                                target_crop_stage: 0,
                                method: ApplicationMethod.InjectedKnifedCoultered,
                                fertilizer_product: "2",
                                user_rate: null,
                                application_cost: null,
                                establish_plots: false,
                                model_rate: null,
                            },
                        ],
            });
        }
    }, [
        applicationPlanForm,
        cropPlansRequest.data,
        cropStageOptions,
        editingPlanId,
        simulationType,
        userOrganizationId,
    ]);

    const handleEditPlan = useCallback(
        (plan: ApplicationPlan) => {
            const planId = plan.id ?? null;
            setEditingPlanId(planId);
            const simulatedPlan = cropPlansRequest.data?.find(
                (cp) =>
                    cp.application_plan?.id === planId &&
                    cp.simulation_type === simulationType,
            );
            applicationPlanForm.reset({
                id: plan.id,
                name: plan.name ?? "",
                organization: plan.organization || userOrganizationId,
                planned_applications:
                    plan.planned_applications?.length > 0
                        ? sortArrayByKey(
                            plan.planned_applications,
                            (app) =>
                                cropStageOptions.find(
                                    (cs) => cs.id === app.target_crop_stage,
                                )?.days_after_planting,
                        ).map((app) => ({
                            id: app.id,
                            target_crop_stage: app.target_crop_stage,
                            method: app.method,
                            fertilizer_product:
                                app.fertilizer_product.toString(),
                            user_rate: app.user_rate,
                            application_cost: app.application_cost,
                            establish_plots: app.establish_plots,
                            model_rate:
                                simulatedPlan?.simulated_rates?.find(
                                    (rate) =>
                                        rate.planned_application === app.id,
                                )?.optimal_rate ?? null,
                        }))
                        : [
                            {
                                id: null,
                                target_crop_stage: 0,
                                method: ApplicationMethod.InjectedKnifedCoultered,
                                fertilizer_product: "2",
                                user_rate: null,
                                application_cost: null,
                                establish_plots: false,
                                model_rate: null,
                            },
                        ],
            });
        },
        [
            applicationPlanForm,
            cropPlansRequest.data,
            cropStageOptions,
            simulationType,
            userOrganizationId,
        ],
    );

    const methodOptions = useMemo(
        () =>
            applicationMethodOptions.map((o) => ({
                id: o.id,
                description: o.description,
            })),
        [],
    );

    const cropStageLookup = useMemo(() => {
        return cropStageOptions.reduce(
            (acc, option) => {
                acc[option.id] = option;
                return acc;
            },
            {} as Record<number, CropStage>,
        );
    }, [cropStageOptions]);

    const stageOptions = useMemo(() => {
        return cropStageOptions.map((o) => ({
            id: o.id,
            description: o.growth_stage,
        }));
    }, [cropStageOptions]);

    const copyPlan = useCallback(
        (plan: ApplicationPlan) => {
            const newPlanData = {
                ...plan,
                planned_applications: plan.planned_applications.map(
                    (application) => ({
                        ...application,
                        id: null,
                    }),
                ),
                id: null,
                name: `${plan.name} (Copy)`,
                organization: userOrganizationId,
            };
            saveApplicationPlanMutation.mutate({
                url: `/application_plans/`,
                method: "POST",
                data: newPlanData,
            });
        },
        [saveApplicationPlanMutation, userOrganizationId],
    );
    const deleteApplicationPlanMutation = useMutateRequest<ApplicationPlan>({
        successMessage: "Application Plan Deleted",
        onSuccess: () => {
            applicationPlansRequest.refetch();
        },
    });

    const { showConfirm } = useConfirm();
    const deletePlan = useCallback(
        (plan: ApplicationPlan) => {
            showConfirm({
                title: "Delete Application Plan",
                description:
                    "Are you sure you want to delete this application plan?",
                onConfirm: async () => {
                    await deleteApplicationPlanMutation.mutateAsync({
                        url: `/application_plans/${plan.id}/`,
                        method: "delete",
                    });
                },
            });
        },
        [deleteApplicationPlanMutation, showConfirm],
    );

    const primaryApplicationPlan = useMemo(() => {
        if (!primaryCropPlan) return null;
        return applicationPlansRequest.data?.find(
            (plan) => plan.id === primaryCropPlan.application_plan?.id,
        )?.id;
    }, [applicationPlansRequest.data, primaryCropPlan]);

    const [selectedApplicationPlan, setSelectedApplicationPlan] =
        useState<ApplicationPlan | null>(null);

    const columns: ColumnDef<ApplicationPlan>[] = useMemo(
        () => [
            applicationPlanColumns.selection(),
            applicationPlanColumns.name(),
            applicationPlanColumns.organization(organizations),
            applicationPlanColumns.applications(cropStageLookup),
            {
                id: "isPrimary",
                header: "Primary",
                accessorFn: (row) => row.id === primaryApplicationPlan,
                cell: ({ getValue }) =>
                    getValue() ? (
                        <div>
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                color="green"
                            />
                        </div>
                    ) : null,
                size: 70,
            },
            {
                id: "actions",
                header: "Actions",
                enableSorting: false,
                cell: ({ row }) => {
                    const isExpanded = row.getIsExpanded();

                    return (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                color="secondary"
                                onPress={() => {
                                    setSelectedApplicationPlan(row.original);
                                }}
                            >
                                Set Primary
                            </Button>
                            <Button
                                size="sm"
                                color="primary"
                                onPress={() => {
                                    if (isExpanded) {
                                        setEditingPlanId(null);
                                    } else {
                                        setAddNewPlan(false);
                                        handleEditPlan(row.original);
                                    }
                                }}
                            >
                                {isExpanded ? "Cancel" : "Edit"}
                            </Button>
                            <Button
                                size="sm"
                                onPress={() => {
                                    copyPlan(row.original);
                                }}
                                variant="light"
                                isIconOnly
                            >
                                <FontAwesomeIcon icon={faCopy} />
                            </Button>
                            {userIsSentinel && (
                                <Button
                                    size="sm"
                                    isIconOnly
                                    variant="light"
                                    color="danger"
                                    onPress={() => {
                                        deletePlan(row.original);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            )}
                        </div>
                    );
                },
                size: 300,
            },
        ],
        [
            copyPlan,
            cropStageLookup,
            deletePlan,
            handleEditPlan,
            organizations,
            primaryApplicationPlan,
            userIsSentinel,
        ],
    );

    const userCanEditApplicationPlan = useMemo(() => {
        return (
            userIsSentinel ||
            (!isGrower &&
                applicationPlanOrganization !== SENTINEL_ORGANIZATION_ID) ||
            applicationPlanOrganization === userOrganizationId
        );
    }, [
        applicationPlanOrganization,
        userOrganizationId,
        userIsSentinel,
        isGrower,
    ]);

    const renderExpandedRow = useCallback(
        (toggleExpanded: () => void) => {
            return (
                <div className="p-4 flex">
                    <FormProvider {...applicationPlanForm}>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="flex flex-row gap-2">
                                    <TextInput
                                        name="name"
                                        label="Plan name"
                                        size="sm"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        isReadOnly={!userCanEditApplicationPlan}
                                    />
                                    {userIsSentinel && (
                                        <AutoCompleteInput
                                            name="organization"
                                            label="Organization"
                                            labelPlacement="outside"
                                            options={organizations}
                                            size="sm"
                                            placeholder=" "
                                        />
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        color="primary"
                                        isLoading={
                                            saveApplicationPlanMutation.isPending ||
                                            simulateCropPlansMutation.isPending
                                        }
                                        onPress={() => {
                                            handleSaveApplicationPlan(
                                                (applicationPlanId) => {
                                                    if (
                                                        !plantingPlan?.id ||
                                                        !applicationPlanId
                                                    )
                                                        return;
                                                    simulateCropPlansMutation.mutate(
                                                        {
                                                            url: "/simulated_crop_plans/bulk_upsert/",
                                                            method: "put",
                                                            data: [
                                                                {
                                                                    field: fields[0]
                                                                        .id,
                                                                    planting_plan:
                                                                        plantingPlan.id,
                                                                    application_plan:
                                                                        applicationPlanId,
                                                                },
                                                            ],
                                                        },
                                                    );
                                                },
                                            );
                                        }}
                                    >
                                        Generate Rates
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onPress={() => {
                                            handleSaveApplicationPlan(() => {
                                                setAddNewPlan(false);
                                                setEditingPlanId(null);
                                                toggleExpanded();
                                            });
                                        }}
                                        isDisabled={
                                            !applicationPlanForm.formState
                                                .isValid ||
                                            !userCanEditApplicationPlan
                                        }
                                        isLoading={
                                            saveApplicationPlanMutation.isPending ||
                                            simulateCropPlansMutation.isPending
                                        }
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="bordered"
                                        size="sm"
                                        onPress={() => {
                                            setEditingPlanId(null);
                                            toggleExpanded();
                                        }}
                                        isDisabled={
                                            saveApplicationPlanMutation.isPending
                                        }
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                            <div className="rounded-lg border border-default-200 overflow-hidden">
                                <div className="grid grid-cols-[auto_1fr_1fr_100px_100px_80px_80px] gap-2 px-3 py-2 bg-default-100/70 text-default-600 text-xs font-medium">
                                    <div className="min-w-[120px]">Stage</div>
                                    <div>Method</div>
                                    <div>Fertilizer</div>
                                    <div>Rate</div>
                                    <div>
                                        Model Rate (
                                        {getSimulationTypeLabel(simulationType)}
                                        )
                                    </div>
                                    <div>Plots</div>
                                    <div></div>
                                </div>
                                {applicationFields.map((field, idx) => (
                                    <div
                                        key={field.id}
                                        className="grid grid-cols-[auto_1fr_1fr_100px_100px_80px_80px] gap-2 px-3 py-2 items-center border-t border-default-100"
                                    >
                                        <div className="min-w-[120px]">
                                            <SelectInput
                                                aria-label="Crop Stage"
                                                name={`planned_applications.${idx}.target_crop_stage`}
                                                options={stageOptions}
                                                size="sm"
                                                hideErrorMessage
                                                isDisabled={
                                                    !userCanEditApplicationPlan
                                                }
                                            />
                                        </div>
                                        <div className="min-w-[140px]">
                                            <SelectInput
                                                aria-label="Application Method"
                                                name={`planned_applications.${idx}.method`}
                                                options={methodOptions}
                                                size="sm"
                                                hideErrorMessage
                                                isDisabled={
                                                    !userCanEditApplicationPlan
                                                }
                                            />
                                        </div>
                                        <div className="min-w-[140px]">
                                            <ProductAutoComplete
                                                isDisabled={
                                                    !userCanEditApplicationPlan
                                                }
                                                selectedProductId={
                                                    applicationPlanForm.watch(
                                                        `planned_applications.${idx}.fertilizer_product`,
                                                    ) || null
                                                }
                                                setSelectedProductId={(
                                                    productId,
                                                ) => {
                                                    applicationPlanForm.setValue(
                                                        `planned_applications.${idx}.fertilizer_product`,
                                                        productId ?? "",
                                                    );
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <NumberInput
                                                aria-label="User Rate"
                                                name={`planned_applications.${idx}.user_rate`}
                                                size="sm"
                                                hideErrorMessage
                                                isReadOnly={
                                                    !userCanEditApplicationPlan
                                                }
                                            />
                                        </div>
                                        <div>
                                            <NumberInput
                                                isReadOnly
                                                aria-label={`Model Rate ${getSimulationTypeLabel(simulationType)}`}
                                                name={`planned_applications.${idx}.model_rate`}
                                                size="sm"
                                                hideErrorMessage
                                                isInvalid={
                                                    simulatedPlanStatus ===
                                                    ProcessingStatus.FAILED ||
                                                    simulatedPlanStatus ===
                                                    ProcessingStatus.PARTIALLY_COMPLETED
                                                }
                                                endContent={
                                                    saveApplicationPlanMutation.isPending ||
                                                        simulateCropPlansMutation.isPending ||
                                                        simulatedPlanStatus ===
                                                        ProcessingStatus.PROCESSING ||
                                                        simulatedPlanStatus ===
                                                        ProcessingStatus.PENDING ? (
                                                        <Spinner size="sm" />
                                                    ) : simulatedPlanStatus ===
                                                        ProcessingStatus.FAILED ||
                                                        simulatedPlanStatus ===
                                                        ProcessingStatus.PARTIALLY_COMPLETED ? (
                                                        <FontAwesomeIcon
                                                            color="red"
                                                            icon={
                                                                faExclamationTriangle
                                                            }
                                                        />
                                                    ) : simulatedPlanStatus ===
                                                        ProcessingStatus.COMPLETED ? (
                                                        <FontAwesomeIcon
                                                            color="green"
                                                            icon={faCheckCircle}
                                                        />
                                                    ) : undefined
                                                }
                                            />
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <SwitchInput
                                                aria-label="Establish Plots"
                                                name={`planned_applications.${idx}.establish_plots`}
                                                isReadOnly={
                                                    !userCanEditApplicationPlan
                                                }
                                            />
                                        </div>
                                        <div>
                                            <Button
                                                aria-label="Remove Application"
                                                size="sm"
                                                variant="light"
                                                color="danger"
                                                isIconOnly
                                                onPress={() => remove(idx)}
                                                isDisabled={
                                                    !userCanEditApplicationPlan
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onPress={() => {
                                        append({
                                            establish_plots: false,
                                            method: ApplicationMethod.InjectedKnifedCoultered,
                                            fertilizer_product: "2",
                                            target_crop_stage:
                                                null as unknown as number,
                                        });
                                    }}
                                >
                                    Add Application
                                </Button>
                            </div>
                        </div>
                    </FormProvider>
                </div>
            );
        },
        [
            append,
            applicationFields,
            applicationPlanForm,
            fields,
            handleSaveApplicationPlan,
            methodOptions,
            organizations,
            plantingPlan.id,
            remove,
            saveApplicationPlanMutation.isPending,
            simulateCropPlansMutation,
            simulatedPlanStatus,
            simulationType,
            stageOptions,
            userCanEditApplicationPlan,
            userIsSentinel,
        ],
    );

    const getIsRowExpanded = useCallback(
        (row: Row<ApplicationPlan>) => {
            if (addNewPlan || editingPlanId === null) return false;
            return row.original.id === editingPlanId;
        },
        [addNewPlan, editingPlanId],
    );

    const data = useMemo(() => {
        return (applicationPlansRequest?.data ?? []).map((plan) => ({
            ...plan,
            isPrimary: primaryCropPlan?.application_plan?.id === plan.id,
        }));
    }, [applicationPlansRequest?.data, primaryCropPlan]);

    const getRowId = useCallback(
        (row: ApplicationPlan) => row.id.toString(),
        [],
    );

    const rowSelection = useMemo(() => {
        if (!selectedApplicationPlanIds.length) return {};
        return selectedApplicationPlanIds.reduce(
            (acc, id) => {
                acc[id.toString()] = true;
                return acc;
            },
            {} as Record<string, boolean>,
        );
    }, [selectedApplicationPlanIds]);

    const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
        (updaterOrValue: Updater<RowSelectionState>) => {
            if (typeof updaterOrValue === "function") {
                const value = updaterOrValue(rowSelection);
                const keys = Object.keys(value).filter((key) => value[key]);
                if (keys.length > 0) {
                    setSelectedApplicationPlanIds(keys.map(Number));
                } else {
                    setSelectedApplicationPlanIds([]);
                }
            }
        },
        [rowSelection, setSelectedApplicationPlanIds],
    );

    return (
        <>
            <FormProvider {...applicationPlanForm}>
                <div className="flex-1">
                    <ExpandingTable
                        getRowId={getRowId}
                        data={data}
                        columns={columns}
                        sorting={[]}
                        setSorting={() => { }}
                        renderExpandedRow={renderExpandedRow}
                        getIsRowExpanded={getIsRowExpanded}
                        onRowSelectionChange={onRowSelectionChange}
                        rowSelection={rowSelection}
                    />
                    <div className="flex pt-2">
                        {addNewPlan && (
                            <div>
                                <div className="font-semibold text-lg ">
                                    Create New Plan
                                </div>
                                {renderExpandedRow(() => {
                                    setAddNewPlan(false);
                                    setEditingPlanId(null);
                                })}
                            </div>
                        )}
                        {!addNewPlan && (
                            <Button
                                color="primary"
                                size="sm"
                                onPress={() => {
                                    setEditingPlanId(null);

                                    applicationPlanForm.reset({
                                        id: null,
                                        organization: userOrganizationId,
                                        name: "",
                                        planned_applications: [
                                            {
                                                id: null,
                                                target_crop_stage: 0,
                                                method: ApplicationMethod.InjectedKnifedCoultered,
                                                fertilizer_product: "2",
                                                user_rate: null,
                                                application_cost: null,
                                                establish_plots: false,
                                            },
                                        ],
                                    });
                                    setAddNewPlan(true);
                                }}
                            >
                                Create New Plan
                            </Button>
                        )}
                    </div>
                </div>
            </FormProvider>
            {selectedApplicationPlan && (
                <ConfirmPrimaryPlan
                    fields={fields}
                    plantingPlan={plantingPlan}
                    applicationPlan={selectedApplicationPlan}
                    cropPlans={cropPlansRequest.data}
                    onSave={() => {
                        setSelectedApplicationPlan(null);
                        cropPlansRequest.refetch();
                    }}
                    buttonSize="sm"
                />
            )}
        </>
    );
}
